// AllAssets.js
import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

/* ── Confirm Modal ── */
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmLabel, confirmVariant, icon }) => {
    useEffect(() => {
        if (!isOpen) return;
        const h = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", h);
        return () => document.removeEventListener("keydown", h);
    }, [isOpen, onClose]);
    if (!isOpen) return null;
    const v = confirmVariant === "warning"
        ? { btn: { background: "#f59e0b", color: "#fff", border: "none" }, iconBg: "#fffbeb", iconColor: "#d97706" }
        : { btn: { background: "#dc3545", color: "#fff", border: "none" }, iconBg: "#fff0f0", iconColor: "#dc3545" };
    return (
        <>
            <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, backdropFilter: "blur(2px)" }} />
            <div role="dialog" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1060, width: "min(440px,90vw)", background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden" }}>
                <div style={{ height: 4, background: confirmVariant === "warning" ? "linear-gradient(90deg,#f59e0b,#fbbf24)" : "linear-gradient(90deg,#dc3545,#e85d6b)" }} />
                <div style={{ padding: "28px 28px 24px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, background: v.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 22, color: v.iconColor }}>{icon}</div>
                        <div>
                            <h5 style={{ margin: 0, fontWeight: 700, fontSize: 17, color: "#111827" }}>{title}</h5>
                            <p style={{ margin: "6px 0 0", fontSize: 14, color: "#6b7280", lineHeight: 1.6 }}>{message}</p>
                        </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
                        <button onClick={onConfirm} style={{ padding: "8px 20px", borderRadius: 7, cursor: "pointer", fontSize: 14, fontWeight: 600, ...v.btn }}>{confirmLabel}</button>
                        <button onClick={onClose} style={{ padding: "8px 20px", borderRadius: 7, cursor: "pointer", background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 14, fontWeight: 500 }}>Cancel</button>
                    </div>
                </div>
            </div>
        </>
    );
};

/* ── Active Filter Chip ── */
const FilterChip = ({ label, onRemove }) => (
    <span style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500,
        background: "#e8f0fe", color: "#1a56db", border: "1px solid #c3d4f9",
    }}>
        {label}
        <span onClick={onRemove} style={{ cursor: "pointer", fontWeight: 700, fontSize: 14, lineHeight: 1, color: "#4b72d0" }}>x</span>
    </span>
);

/* ── Constants ── */
const CATEGORIES = ["IT Assets", "Service Industry", "Furniture", "Facility"];
const STATUSES = ["Available", "Allocated", "In Repair", "Disposed", "In Warehouse"];
const STATUS_BADGE = {
    "Available": "bg-success",
    "Allocated": "bg-primary",
    "In Repair": "bg-warning text-dark",
    "Disposed": "bg-secondary",
    "In Warehouse": "bg-info text-dark",
};
const PAGE_SIZES = [10, 25, 50, 100, 500];
const COL_COUNT = 12;

/* ════════════════════════════════════════════ */
export default function AllAssets() {
    const navigate = useNavigate();

    /* Raw data - fetched once, all filtering done client-side */
    const [allAssets, setAllAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [stats, setStats] = useState({ total: 0, available: 0, allocated: 0, inRepair: 0 });

    /* All filter states */
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [subCategoryFilter, setSubCategoryFilter] = useState("");
    const [locationFilter, setLocationFilter] = useState("");
    const [warrantyFilter, setWarrantyFilter] = useState("");
    const [assignedFilter, setAssignedFilter] = useState("");
    const [employeeFilter, setEmployeeFilter] = useState("");
    const [allEmployees, setAllEmployees] = useState([]);
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [empDropdownOpen, setEmpDropdownOpen] = useState(false);
    const token = localStorage.getItem("token");
    const [pageSize, setPageSize] = useState(10);

    /* Pagination (client-side) */
    const [currentPage, setCurrentPage] = useState(0);

    /* Sorting */
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    /* UI */
    const [showFilters, setShowFilters] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [modal, setModal] = useState({ isOpen: false, type: null, asset: null });

    /* Bootstrap CSS */
    useEffect(() => {
        const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
        const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
        return () => { bl.remove(); ic.remove(); };
    }, []);

    /* Auth guard */
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    }, [navigate]);

    /* Initial load */
    useEffect(() => { fetchAllAssets(); fetchStats(); }, []);

    useEffect(() => { setCurrentPage(0); }, [pageSize]);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/employees/active`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await res.json();
                setAllEmployees(Array.isArray(data) ? data : data.content || []);
            } catch { setAllEmployees([]); }
        };
        if (token) load();
    }, [token]);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/api/assets/stats`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) setStats(await res.json());
        } catch (_) { }
    };

    /*
     * Fetch ALL assets in one request (size=10000).
     * Every filter, sort, and page runs purely on the client from this snapshot.
     * This guarantees ALL filter combinations work correctly with zero backend changes.
     */
    const fetchAllAssets = async () => {
        setLoading(true);
        setError("");
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/api/assets?page=0&size=10000`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to fetch assets");
            const data = await res.json();
            setAllAssets(data.content || []);
        } catch (err) { setError(err.message); }
        finally { setLoading(false); }
    };

    /* Dynamic dropdown options derived from the full dataset */
    const locationOptions = useMemo(
        () => [...new Set(allAssets.map(a => a.location).filter(Boolean))].sort(),
        [allAssets]
    );
    const subCategoryOptions = useMemo(
        () => [...new Set(allAssets.map(a => a.subCategory).filter(Boolean))].sort(),
        [allAssets]
    );

    /*
     * CLIENT-SIDE FILTERING
     * Single .filter() pass evaluates every active filter together.
     * Filters are AND-ed: an asset must satisfy ALL active conditions.
     */
    const filteredAssets = useMemo(() => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const in30Days = new Date(today); in30Days.setDate(today.getDate() + 30);
        const q = search.toLowerCase().trim();

        return allAssets.filter(asset => {

            /* 1. Keyword search across name, code, serial, brand */
            if (q) {
                const hay = [asset.assetName, asset.assetCode, asset.serialNumber, asset.brand]
                    .map(v => (v || "").toLowerCase()).join(" ");
                if (!hay.includes(q)) return false;
            }

            /* 2. Category */
            if (categoryFilter && asset.category !== categoryFilter) return false;

            /* 3. Sub-Category */
            if (subCategoryFilter && asset.subCategory !== subCategoryFilter) return false;

            /* 4. Status */
            if (statusFilter && asset.status !== statusFilter) return false;

            /* 5. Location */
            if (locationFilter && asset.location !== locationFilter) return false;

            /* 6. Assignment */
            if (assignedFilter) {
                const isAssigned = Boolean(asset.assignedToName);
                if (assignedFilter === "Assigned" && !isAssigned) return false;
                if (assignedFilter === "Unassigned" && isAssigned) return false;
            }

            /* 7. Warranty status */
            if (warrantyFilter) {
                if (!asset.warrantyEndDate) return false;
                const expiry = new Date(asset.warrantyEndDate); expiry.setHours(0, 0, 0, 0);
                if (warrantyFilter === "Active" && expiry <= today) return false;
                if (warrantyFilter === "Expired" && expiry > today) return false;
                if (warrantyFilter === "Expiring Soon (30 days)" && !(expiry > today && expiry <= in30Days)) return false;
            }


            /* 8. Employee filter */
            if (employeeFilter) {
                if ((asset.assignedToName || "") !== employeeFilter) return false;
            }

            return true;
        });
    }, [
        allAssets, search, categoryFilter, subCategoryFilter,
        statusFilter, locationFilter, assignedFilter,
        warrantyFilter, employeeFilter,
    ]);

    /* CLIENT-SIDE SORTING */
    const sortedAssets = useMemo(() => {
        if (!sortConfig.key) return filteredAssets;
        return [...filteredAssets].sort((a, b) => {
            const av = (a[sortConfig.key] || "").toString().toLowerCase();
            const bv = (b[sortConfig.key] || "").toString().toLowerCase();
            if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
            if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredAssets, sortConfig]);

    /* CLIENT-SIDE PAGINATION */
    const totalPages = Math.ceil(sortedAssets.length / pageSize);
    const pagedAssets = sortedAssets.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

    /* Reset to page 1 whenever any filter changes */
    useEffect(() => { setCurrentPage(0); }, [
        search, categoryFilter, subCategoryFilter, statusFilter,
        locationFilter, assignedFilter, warrantyFilter, employeeFilter,
    ]);
    /* Helpers */
    const handleSort = (key) => setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
    const getSortArrow = (key) => sortConfig.key !== key ? "" : sortConfig.direction === "asc" ? " up" : " down";

    const clearAllFilters = () => {
        setSearch(""); setCategoryFilter(""); setSubCategoryFilter("");
        setStatusFilter(""); setLocationFilter(""); setAssignedFilter("");
        setWarrantyFilter(""); setEmployeeFilter(""); setEmployeeSearch("");
    };

    const confirmDelete = async (asset) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/api/assets/${asset.id}`, {
                method: "DELETE", headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || "Failed to delete asset.");
            }
            toast.success(`Asset "${asset.assetName}" deleted.`);
            setAllAssets(prev => prev.filter(a => a.id !== asset.id));
            fetchStats();
        } catch (err) { toast.error("Failed to delete asset."); }
    };

    const downloadReport = async () => {
        try {
            setDownloading(true);
            const token = localStorage.getItem("token");
            const params = new URLSearchParams({ format: "xlsx" });
            if (search)            params.set("search",      search);
            if (categoryFilter)    params.set("category",    categoryFilter);
            if (subCategoryFilter) params.set("subCategory", subCategoryFilter);
            if (statusFilter)      params.set("status",      statusFilter);
            if (locationFilter)    params.set("location",    locationFilter);
            if (assignedFilter)    params.set("assigned",    assignedFilter);
            if (warrantyFilter)    params.set("warranty",    warrantyFilter);
            if (employeeFilter)    params.set("employee",    employeeFilter);
            const res = await fetch(`${API_BASE_URL}/api/assets/report?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to download report");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const date = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = activeCount > 0
                ? `Asset_Register_Filtered_${date}.xlsx`
                : `Asset_Register_${date}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Downloaded successfully");
        } catch (err) {
            toast.error(err.message || "Failed to download");
        } finally {
            setDownloading(false);
        }
    };

    /* Active filter chips */
    const activeChips = [
        search && { label: `Search: "${search}"`, clear: () => setSearch("") },
        categoryFilter && { label: `Category: ${categoryFilter}`, clear: () => setCategoryFilter("") },
        subCategoryFilter && { label: `Sub-cat: ${subCategoryFilter}`, clear: () => setSubCategoryFilter("") },
        statusFilter && { label: `Status: ${statusFilter}`, clear: () => setStatusFilter("") },
        locationFilter && { label: `Location: ${locationFilter}`, clear: () => setLocationFilter("") },
        assignedFilter && { label: `Assignment: ${assignedFilter}`, clear: () => setAssignedFilter("") },
        warrantyFilter && { label: `Warranty: ${warrantyFilter}`, clear: () => setWarrantyFilter("") },
        employeeFilter && { label: `Employee: ${employeeFilter}`, clear: () => setEmployeeFilter("") },
    ].filter(Boolean);

    const activeCount = activeChips.length;

    /* ═══════════════════════════ RENDER ═══════════════════════════ */
    return (
        <>
            <div className="fixed-top"><AssetModuleNavbar /></div>
            <div className="container-fluid my-4" style={{ paddingTop: "60px" }}></div>

            <div className="d-flex flex-column min-vh-100">
                <div className="container-fluid p-3 bg-light flex-grow-1">

                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="fw-bold mb-0">
                            <i className="bi bi-box-seam me-2"></i> Asset Register
                        </h4>
                        <div className="d-flex gap-2 flex-wrap">
                            <button className="btn btn-primary btn-sm" onClick={() => navigate("/addasset")}>
                                <i className="bi bi-plus-circle me-1"></i> New Asset
                            </button>
                            {/* <button className="btn btn-outline-primary btn-sm" onClick={() => navigate("/newallocation")}>
                                <i className="bi bi-person-plus me-1"></i> Allocate
                            </button> */}
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/allallocations")}>
                                <i className="bi bi-person-check me-1"></i> Allocations
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/alltransfers")}>
                                <i className="bi bi-arrow-left-right me-1"></i> Transfers
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/allmaintenance")}>
                                <i className="bi bi-tools me-1"></i> Maintenance
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/pendingapprovals")}>
                                <i className="bi bi-hourglass-split me-1"></i> Approvals
                            </button>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="row g-2 mb-3">
                        {[
                            { label: "Total Assets",  icon: "bi-box-seam",     color: "#1e3a5f", value: stats.total,     href: null },
                            { label: "Available",     icon: "bi-check-circle", color: "#198754", value: stats.available, href: null },
                            { label: "Allocated",     icon: "bi-person-check", color: "#0d6efd", value: stats.allocated, href: "/allallocations" },
                            { label: "In Repair",     icon: "bi-tools",        color: "#fd7e14", value: stats.inRepair,  href: "/allmaintenance" },
                        ].map((s, i) => (
                            <div className="col-md-3 col-6" key={i}>
                                <div className="bg-white border rounded p-2 d-flex align-items-center gap-2"
                                    style={{ cursor: s.href ? "pointer" : "default" }}
                                    onClick={() => s.href && navigate(s.href)}>
                                    <i className={`bi ${s.icon}`} style={{ color: s.color, fontSize: 20 }}></i>
                                    <div>
                                        <div style={{ fontSize: 11, color: "#6b7280" }}>{s.label}</div>
                                        <div className="fw-bold" style={{ fontSize: 18, color: s.color }}>{s.value ?? 0}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Filter Panel Toggle */}
                    <div
                        className="d-flex justify-content-between align-items-center p-2 bg-white border mb-0"
                        style={{ cursor: "pointer", borderRadius: showFilters ? "6px 6px 0 0" : 6 }}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <div className="d-flex align-items-center gap-2">
                            {/* <i className={`bi bi-funnel${activeCount ? "-fill" : ""} text-primary`}></i> */}
                            <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
                            {activeCount > 0 && (
                                <span className="badge bg-primary rounded-pill" style={{ fontSize: 11 }}>{activeCount} active</span>
                            )}
                        </div>
                        <i className={`bi bi-chevron-${showFilters ? "up" : "down"} text-muted`}></i>
                    </div>

                    {/* Filter Panel Body */}
                    {showFilters && (
                        <div className="bg-white border border-top-0 rounded-bottom p-3 mb-2">

                            {/* Row 1: Keyword, Category, Sub-Category, Status, Location */}
                            <div className="row g-2 mb-2">
                                <div className="col-md-3">
                                    <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                        <i className="bi bi-search me-1"></i>Keyword Search
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Name, code, serial, brand..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                        <i className="bi bi-grid me-1"></i>Category
                                    </label>
                                    <select className="form-select form-select-sm" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                                        <option value="">All Categories</option>
                                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                        <i className="bi bi-diagram-3 me-1"></i>Sub-Category
                                    </label>
                                    <select className="form-select form-select-sm" value={subCategoryFilter} onChange={e => setSubCategoryFilter(e.target.value)}>
                                        <option value="">All Sub-Categories</option>
                                        {subCategoryOptions.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                        <i className="bi bi-tag me-1"></i>Status
                                    </label>
                                    <select className="form-select form-select-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                        <option value="">All Statuses</option>
                                        {STATUSES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                        <i className="bi bi-geo-alt me-1"></i>Location
                                    </label>
                                    <select className="form-select form-select-sm" value={locationFilter} onChange={e => setLocationFilter(e.target.value)}>
                                        <option value="">All Locations</option>
                                        {locationOptions.map(l => <option key={l}>{l}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Row 2: Warranty Status, Assignment, Warranty Date Range, Clear */}
                            <div className="row g-2 mb-3">
                                <div className="col-md-2">
                                    <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                        <i className="bi bi-shield-check me-1"></i>Warranty Status
                                    </label>
                                    <select className="form-select form-select-sm" value={warrantyFilter} onChange={e => setWarrantyFilter(e.target.value)}>
                                        <option value="">All Warranties</option>
                                        <option value="Active">Active</option>
                                        <option value="Expired">Expired</option>
                                        <option value="Expiring Soon (30 days)">Expiring Soon (30 days)</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                        <i className="bi bi-person-badge me-1"></i>Assignment
                                    </label>
                                    <select className="form-select form-select-sm" value={assignedFilter} onChange={e => setAssignedFilter(e.target.value)}>
                                        <option value="">All</option>
                                        <option value="Assigned">Assigned</option>
                                        <option value="Unassigned">Unassigned</option>
                                    </select>
                                </div>
                                <div className="col-md-4" style={{ position: "relative" }}>
                                    <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                        <i className="bi bi-person me-1"></i>Employee
                                    </label>
                                    <div style={{ position: "relative" }}>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Search employee..."
                                            value={employeeSearch || employeeFilter}
                                            onFocus={() => { setEmpDropdownOpen(true); setEmployeeSearch(""); }}
                                            onChange={e => { setEmployeeSearch(e.target.value); setEmployeeFilter(""); setEmpDropdownOpen(true); }}
                                            onBlur={() => setTimeout(() => setEmpDropdownOpen(false), 200)}
                                            style={{ fontSize: 12, paddingRight: 28 }}
                                            autoComplete="off"
                                        />
                                        {(employeeFilter || employeeSearch) && (
                                            <span
                                                onMouseDown={e => { e.preventDefault(); setEmployeeFilter(""); setEmployeeSearch(""); setEmpDropdownOpen(false); }}
                                                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#9ca3af", fontSize: 14, lineHeight: 1 }}
                                            >✕</span>
                                        )}
                                    </div>
                                    {empDropdownOpen && (
                                        <div style={{
                                            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 999,
                                            background: "#fff", border: "1px solid #d1d5db", borderRadius: 6,
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: 200,
                                            overflowY: "auto", marginTop: 2,
                                        }}>
                                            <div
                                                onMouseDown={() => { setEmployeeFilter(""); setEmployeeSearch(""); setEmpDropdownOpen(false); }}
                                                style={{ padding: "6px 12px", fontSize: 12, color: "#6b7280", cursor: "pointer", borderBottom: "1px solid #f3f4f6" }}
                                                className="dropdown-item-hover"
                                            >
                                                All Employees
                                            </div>
                                            {allEmployees
                                                .filter(emp =>
                                                    `${emp.firstName} ${emp.lastName} ${emp.employeeId}`
                                                        .toLowerCase()
                                                        .includes((employeeSearch || "").toLowerCase().trim())
                                                )
                                                .map(emp => {
                                                    const fullName = `${emp.firstName} ${emp.lastName}`;
                                                    const isSelected = employeeFilter === fullName;
                                                    return (
                                                        <div
                                                            key={emp.id}
                                                            onMouseDown={() => { setEmployeeFilter(fullName); setEmployeeSearch(""); setEmpDropdownOpen(false); }}
                                                            style={{
                                                                padding: "6px 12px", fontSize: 12, cursor: "pointer",
                                                                background: isSelected ? "#e8f0fe" : "transparent",
                                                                color: isSelected ? "#1a56db" : "#111827",
                                                                fontWeight: isSelected ? 600 : 400,
                                                            }}
                                                        >
                                                            {fullName}
                                                            
                                                        </div>
                                                    );
                                                })
                                            }
                                            {allEmployees.filter(emp =>
                                                `${emp.firstName} ${emp.lastName} ${emp.employeeId}`
                                                    .toLowerCase()
                                                    .includes((employeeSearch || "").toLowerCase().trim())
                                            ).length === 0 && (
                                                    <div style={{ padding: "8px 12px", fontSize: 12, color: "#9ca3af", textAlign: "center" }}>
                                                        No employees found
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-4 d-flex align-items-end">
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={clearAllFilters}
                                        disabled={activeCount === 0}
                                    >
                                        <i className="bi bi-x-circle me-1"></i>Clear All Filters
                                        {activeCount > 0 && (
                                            <span className="badge bg-danger ms-1 rounded-pill">{activeCount}</span>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Active filter chips */}
                            {activeChips.length > 0 && (
                                <div className="d-flex flex-wrap gap-1 pt-2 border-top">
                                    <span style={{ fontSize: 12, color: "#6b7280", alignSelf: "center" }}>Active:</span>
                                    {activeChips.map((chip, i) => (
                                        <FilterChip key={i} label={chip.label} onRemove={chip.clear} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}


                    {/* Toolbar */}
                    <div className="d-flex justify-content-between align-items-center p-2 bg-light border mb-2 mt-2">
                        <div className="d-flex align-items-center gap-2">
                            <button className="btn btn-sm btn-outline-secondary" onClick={downloadReport} disabled={downloading}>
                                <i className="bi bi-download me-1"></i>{downloading ? "Downloading..." : "Export Excel"}
                            </button>
                            {/* ← NEW: rows-per-page dropdown */}
                            <div className="d-flex align-items-center gap-1">
                                <label style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>Rows per page:</label>
                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: "auto", fontSize: 13 }}
                                    value={pageSize}
                                    onChange={e => setPageSize(Number(e.target.value))}
                                >
                                    {PAGE_SIZES.map(n => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <span className="text-muted" style={{ fontSize: 13 }}>
                            {filteredAssets.length} asset{filteredAssets.length !== 1 ? "s" : ""}
                            {totalPages > 1 ? ` - Page ${currentPage + 1} of ${totalPages}` : ""}
                            {activeCount > 0 && allAssets.length > 0 && (
                                <span className="text-primary ms-2">(filtered from {allAssets.length} total)</span>
                            )}
                        </span>
                    </div>

                    {/* Table */}
                    <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "60vh", border: "1px solid #dee2e6", borderRadius: 4 }}>
                        <table className="table table-sm align-middle text-start">
                            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 2 }}>
                                <tr>
                                    <th style={{ width: 100 }}>View | Edit</th>
                                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("assetCode")}>Asset Code {getSortArrow("assetCode")}</th>
                                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("assetName")}>Asset Name {getSortArrow("assetName")}</th>
                                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("category")}>Category {getSortArrow("category")}</th>
                                    <th>Sub-Category</th>
                                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("brand")}>Brand/Model {getSortArrow("brand")}</th>
                                    <th>Serial No.</th>
                                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("status")}>Status {getSortArrow("status")}</th>
                                    <th>Location</th>
                                    <th>Assigned To</th>
                                    <th>Warranty Expiry</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={COL_COUNT} className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading assets...</div>
                                        </td>
                                    </tr>
                                ) : pagedAssets.length === 0 ? (
                                    <tr>
                                        <td colSpan={COL_COUNT} className="text-center text-muted py-5">
                                            <i className="bi bi-funnel" style={{ fontSize: 32, opacity: 0.3 }}></i>
                                            <div className="mt-2">
                                                {allAssets.length === 0
                                                    ? <span>No assets found. <Link to="/addasset">Add your first asset</Link></span>
                                                    : <span>No assets match the current filters.{" "}
                                                        <button className="btn btn-sm btn-link p-0" onClick={clearAllFilters}>Clear filters</button>
                                                    </span>
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                ) : pagedAssets.map(asset => (
                                    <tr key={asset.id}>
                                        <td>
                                            <Link to={`/viewasset/${asset.id}`} className="me-1 text-primary" style={{ fontSize: 13 }}>View</Link>|
                                            <Link to={`/editasset/${asset.id}`} className="ms-1 text-primary" style={{ fontSize: 13 }}>Edit</Link>
                                        </td>
                                        <td>
                                            <span className="badge" style={{ background: "#e8f0f8", color: "#1e3a5f", border: "1px solid #c8d8e8", fontFamily: "monospace" }}>
                                                {asset.assetCode}
                                            </span>
                                        </td>
                                        <td style={{ maxWidth: 180 }}>
                                            <div className="fw-semibold" style={{ fontSize: 13 }}>{asset.assetName}</div>
                                        </td>
                                        <td style={{ fontSize: 13 }}>{asset.category}</td>
                                        <td style={{ fontSize: 13 }}>{asset.subCategory}</td>
                                        <td style={{ fontSize: 13 }}>
                                            <div>{asset.brand}</div>
                                            <div className="text-muted" style={{ fontSize: 11 }}>{asset.modelNumber}</div>
                                        </td>
                                        <td><small className="text-muted font-monospace">{asset.serialNumber}</small></td>
                                        <td>
                                            <span className={`badge ${STATUS_BADGE[asset.status] || "bg-secondary"}`} style={{ fontSize: 11 }}>
                                                {asset.status}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: 13 }}>{asset.location || "—"}</td>
                                        <td style={{ fontSize: 13 }}>{asset.assignedToName || <span className="text-muted">—</span>}</td>
                                        <td style={{ fontSize: 12 }}>
                                            {asset.warrantyEndDate
                                                ? <span className={new Date(asset.warrantyEndDate) < new Date() ? "text-danger" : "text-muted"}>{asset.warrantyEndDate}</span>
                                                : "—"}
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                {/* <button className="btn btn-sm btn-outline-warning" onClick={() => navigate(`/editasset/${asset.id}`)} title="Edit">
                                                    <i className="bi bi-pencil"></i>
                                                </button> */}
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => setModal({ isOpen: true, type: "delete", asset })} title="Delete">
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {!loading && totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(0)} disabled={currentPage === 0}>«</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 0}>Prev</button>
                            <span className="text-muted" style={{ fontSize: 14 }}>Page {currentPage + 1} of {totalPages}</span>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages - 1}>Next</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage >= totalPages - 1}>»</button>
                        </div>
                    )}

                    {error && <p className="text-danger mt-3">{error}</p>}
                </div>
            </div>

            <ConfirmModal
                isOpen={modal.isOpen}
                onClose={() => setModal({ isOpen: false, type: null, asset: null })}
                onConfirm={() => { if (modal.type === "delete") confirmDelete(modal.asset); setModal({ isOpen: false, type: null, asset: null }); }}
                title="Delete Asset"
                message={`Are you sure you want to delete "${modal.asset?.assetName}"? This action cannot be undone.`}
                confirmLabel="Delete"
                confirmVariant="danger"
                icon="delete"
            />
        </>
    );
}

